/**
 * The opening AI Insights message for a report whose chat has no history yet.
 *
 * Why this exists: the initial draft is produced by the report-writer TASK
 * (POST /v0/report-writer -> run_report_writer_task), which never creates a chat thread. So the
 * first time the user lands on the editor, apiGetThreads({reportDocId}) returns [] and the
 * AI Insights column showed only the generic empty-chat tagline - no acknowledgement that a
 * report had just been written, and no hint that this chat is how you change it.
 *
 * Everything here is derived from real data (the reportDoc + the generation task's own
 * outputs.outline / outputs.section_results - the same two fields ReportSectionChecklist
 * renders), so the message never claims sections or sources that don't exist.
 */

// The LifecycleState.ERROR value, inlined rather than imported: this module is unit-tested, and
// vitest does not resolve the src-rooted "api/common/..." import style (see frontend/CLAUDE.md's
// "Vitest imports from src/utils" note). One string constant is not worth a test-only shim.
const STATE_ERROR = "ERROR";

/** Sections the plan produced, split into done/failed the same way ReportSectionChecklist does. */
export function summariseSections(task) {
  const outline = task?.outputs?.outline;
  if (!outline?.length) {
    return { total: 0, done: 0, failedTitles: [] };
  }
  const sectionResults = task?.outputs?.section_results || {};
  let done = 0;
  const failedTitles = [];
  outline.forEach((section) => {
    const result = sectionResults[section.section_id];
    // action "keep" = the planner left an existing section untouched: done, and never a failure.
    if (section.action === "keep" || result?.success) {
      done += 1;
    } else if (result && !result.success) {
      failedTitles.push(section.title || section.section_id);
    }
  });
  return { total: outline.length, done, failedTitles };
}

const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * @param {Object} reportDoc as loaded by reportDocService.get()
 * @param {Object} [task] the generation task (GET /v0/task/{reportDoc.taskId}), if loaded
 * @returns {string|null} markdown, or null when there is nothing generated to talk about yet
 */
export function buildReportIntroMessage(reportDoc, task) {
  if (!reportDoc) return null;
  const isError = reportDoc.state === STATE_ERROR;
  // Nothing drafted and no error to explain -> leave the standard empty-chat state alone.
  if (!reportDoc.content && !isError) return null;

  const name = reportDoc.title ? `**${reportDoc.title}**` : "Your report";
  const { total, done, failedTitles } = summariseSections(task);
  const sourceCount = reportDoc.sourcesAllowed?.length || 0;
  const from = sourceCount ? ` from ${plural(sourceCount, "source")}` : "";

  const lines = [];
  if (isError) {
    lines.push(
      failedTitles.length
        ? `${name} ran into a problem while generating. ${plural(failedTitles.length, "section")} couldn't be written: ${failedTitles.join(", ")}.`
        : `${name} ran into a problem while generating.${reportDoc.content ? " There is still usable content in the draft on the right." : ""}`
    );
    lines.push("Ask me to retry the report, or to write a specific section, and I'll update the draft.");
  } else if (failedTitles.length) {
    lines.push(
      `${name} is drafted, but ${failedTitles.length} of ${plural(total, "section")} couldn't be written: ${failedTitles.join(", ")}.`
    );
    lines.push("Ask me to retry those sections, or to change anything else in the draft.");
  } else {
    lines.push(total ? `${name} is ready — I drafted ${plural(done, "section")}${from}.` : `${name} is ready.`);
    lines.push("Ask me to change anything, for example:");
    lines.push(
      ["- Expand or rewrite a section", "- Tighten the executive summary", "- Check a figure against the sources"].join("\n")
    );
  }
  lines.push("I edit the report itself, so my changes appear in the document on the right.");
  return lines.join("\n\n");
}

/**
 * The same, shaped for AIChatWidget's `initialMessages` prop.
 * `isIntro` marks it as a seeded greeting rather than real conversation history, so
 * "show on a fresh chat only" contextual prompts (rules.chatShowWhen === "initial") still show.
 * @returns {Array|undefined} undefined when there is no intro to show
 */
export function buildReportIntroMessages(reportDoc, task) {
  const content = buildReportIntroMessage(reportDoc, task);
  if (!content) return undefined;
  return [{ role: "assistant", content, isIntro: true }];
}
